import { ScheduleException } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import { Schedule } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RRule } from 'rrule';
import { AvailabilityExceptionInfo, CalendarStripeLineInfo } from '../../components/calendar-view/types';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export type CalendarEntitySchedule = Partial<Schedule> & {
  id: string;
  locationId: string;
  resolvedScheduleDefinitions?: CalendarStripeLineInfo[];
  resolvedExceptions?: AvailabilityExceptionInfo[];
};

type ScheduleDefinitionGroup = {
  locationId?: string;
  entityId: string;
  scheduleDefinitions?: ScheduleDefinition[];
};

type ScheduleExceptionEntityType = 'PROVIDER' | 'OPERATORY';

const DISPLAY_TIME_FORMAT = 'hh:mm A';

const getCalendarDateInTimezone = (calendarDate: string, timezoneId = dayjs.tz.guess()) =>
  dayjs.tz(calendarDate, 'MM/DD/YYYY', timezoneId).format('YYYY-MM-DD');

// RRule uses these UTC components as floating wall-clock fields for date matching.
const getRRuleWallTime = (date: string, time = '00:00') =>
  dayjs.utc(`${date}T${time.length === 5 ? `${time}:00` : time}`);

// '24:00' represents end-of-day and rolls over to midnight of the next anchor day.
const getTimeDateTime = (time: string) =>
  time === '24:00'
    ? dayjs.utc('2000-01-02T00:00:00')
    : dayjs.utc(`2000-01-01T${time.length === 5 ? `${time}:00` : time}`);

const getDurationFromTimeRange = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return 0;
  }

  const start = getTimeDateTime(startTime);
  const end = getTimeDateTime(endTime);
  return Math.max(end.diff(start, 'minute'), 0);
};

const doesScheduleDefinitionApplyOnDate = (scheduleDefinition: ScheduleDefinition, calendarDate: string) => {
  if (!scheduleDefinition.startDate) {
    return false;
  }

  const localCalendarDate = getCalendarDateInTimezone(calendarDate, scheduleDefinition.timezone);

  if (localCalendarDate < scheduleDefinition.startDate) {
    return false;
  }

  if (scheduleDefinition.endDate && localCalendarDate > scheduleDefinition.endDate) {
    return false;
  }

  if (!scheduleDefinition.rRule?.trim()) {
    return localCalendarDate === scheduleDefinition.startDate;
  }

  try {
    const rule = new RRule({
      ...RRule.parseString(scheduleDefinition.rRule),
      dtstart: getRRuleWallTime(scheduleDefinition.startDate, scheduleDefinition.startTime).toDate(),
      ...(scheduleDefinition.endDate
        ? {
            until: getRRuleWallTime(scheduleDefinition.endDate, scheduleDefinition.endTime || '23:59').toDate(),
          }
        : {}),
    });

    const startOfDay = dayjs.utc(localCalendarDate).startOf('day').toDate();
    const endOfDay = dayjs.utc(localCalendarDate).endOf('day').toDate();

    const between = rule.between(startOfDay, endOfDay, true);
    return between.length > 0;
  } catch (e) {
    return localCalendarDate === scheduleDefinition.startDate;
  }
};

export const toScheduleDefinitionBlock = (scheduleDefinition: ScheduleDefinition): CalendarStripeLineInfo | null => {
  const duration = getDurationFromTimeRange(scheduleDefinition.startTime, scheduleDefinition.endTime);
  if (!scheduleDefinition.startTime || duration <= 0) {
    return null;
  }

  return {
    startTime: getTimeDateTime(scheduleDefinition.startTime).format(DISPLAY_TIME_FORMAT),
    duration,
  };
};

const getScheduleExceptionBreakInfo = (
  scheduleException: ScheduleException,
  calendarDate: string,
  timezoneId: string
): AvailabilityExceptionInfo | null => {
  if (scheduleException.recurrenceType === 'SINGLE_OCCURRENCE') {
    const startDate = getScheduleExceptionStartDate(scheduleException, timezoneId);
    const endDate = scheduleException.endDate || startDate;
    const startTime = scheduleException.time?.startTime;
    const endTime = scheduleException.time?.endTime;
    const localCalendarDate = getCalendarDateInTimezone(calendarDate, timezoneId);

    if (!startDate || !startTime || !endTime) {
      return {
        startTime: '12:00 AM',
        duration: 24 * 60,
        scheduleException,
      };
    }

    const visibleStart = localCalendarDate === startDate ? startTime : '00:00';
    const visibleEnd = localCalendarDate === endDate ? endTime : '24:00';
    const duration = getDurationFromTimeRange(visibleStart, visibleEnd);

    if (duration <= 0) {
      return null;
    }

    return {
      startTime: getTimeDateTime(visibleStart).format(DISPLAY_TIME_FORMAT),
      duration,
      scheduleException,
    };
  }

  if (!scheduleException.time?.startTime || !scheduleException.time?.endTime) {
    // all day exception
    return {
      startTime: '12:00 AM',
      duration: 24 * 60,
      scheduleException,
    };
  }
  const duration = getDurationFromTimeRange(scheduleException.time.startTime, scheduleException.time.endTime);
  if (duration <= 0) {
    return null;
  }

  return {
    startTime: getTimeDateTime(scheduleException.time.startTime).format(DISPLAY_TIME_FORMAT),
    duration,
    scheduleException,
  };
};

const getScheduleExceptionStartDate = (scheduleException: ScheduleException, timezoneId: string) => {
  if (!scheduleException.nextOccurrenceStart) {
    return '';
  }

  return dayjs(scheduleException.nextOccurrenceStart).tz(timezoneId).format('YYYY-MM-DD');
};

const doesScheduleExceptionApplyOnDate = (
  scheduleException: ScheduleException,
  calendarDate: string,
  timezoneId: string
) => {
  const startDate = getScheduleExceptionStartDate(scheduleException, timezoneId);
  if (!startDate) {
    return false;
  }

  const localCalendarDate = getCalendarDateInTimezone(calendarDate, timezoneId);

  if (localCalendarDate < startDate) {
    return false;
  }

  if (scheduleException.recurrenceType === 'SINGLE_OCCURRENCE') {
    return localCalendarDate <= (scheduleException.endDate || startDate);
  }

  if (scheduleException.endDate && localCalendarDate > scheduleException.endDate) {
    return false;
  }

  if (!scheduleException.recurrenceRule?.trim()) {
    return scheduleException.endDate ? localCalendarDate <= scheduleException.endDate : localCalendarDate === startDate;
  }

  try {
    const rule = new RRule({
      ...RRule.parseString(scheduleException.recurrenceRule),
      dtstart: getRRuleWallTime(startDate, scheduleException.time?.startTime || '00:00').toDate(),
      ...(scheduleException.endDate
        ? {
            until: getRRuleWallTime(scheduleException.endDate, scheduleException.time?.endTime || '23:59').toDate(),
          }
        : {}),
    });

    const startOfDay = dayjs.utc(localCalendarDate).startOf('day').toDate();
    const endOfDay = dayjs.utc(localCalendarDate).endOf('day').toDate();
    return rule.between(startOfDay, endOfDay, true).length > 0;
  } catch (e) {
    return localCalendarDate === startDate;
  }
};

const doesScheduleExceptionMatchEntity = (
  scheduleException: ScheduleException,
  entityId: string,
  entityType: ScheduleExceptionEntityType
) => {
  if (scheduleException.target?.scopeType === 'LOCATION') {
    return true;
  }

  if (scheduleException.target?.scopeType !== 'RESOURCE') {
    return false;
  }

  // We were not considering currently logicOperator === 'ALL' scenarios. Only 'ANY' is handled.
  // Need to co-ordinate with backend as they also need to handle 'ALL' scenarios. ("Apply to combinations" option in the UI)
  if (scheduleException.target.logicOperator !== 'ANY') {
    return false;
  }

  return (scheduleException.target?.resources ?? []).some(
    (resource) => resource.type === entityType && resource.id === entityId
  );
};

const getEntityAvailabilityExceptionsFromScheduleExceptions = ({
  scheduleExceptions,
  entityId,
  entityType,
  calendarDate,
  timezoneId,
}: {
  scheduleExceptions: ScheduleException[];
  entityId: string;
  entityType: ScheduleExceptionEntityType;
  calendarDate: string;
  timezoneId: string;
}): AvailabilityExceptionInfo[] => {
  return scheduleExceptions
    .filter((scheduleException) => doesScheduleExceptionMatchEntity(scheduleException, entityId, entityType))
    .filter((scheduleException) => doesScheduleExceptionApplyOnDate(scheduleException, calendarDate, timezoneId))
    .map((scheduleException) => getScheduleExceptionBreakInfo(scheduleException, calendarDate, timezoneId))
    .filter((breakInfo): breakInfo is AvailabilityExceptionInfo => breakInfo !== null)
    .sort((left, right) => left.startTime.localeCompare(right.startTime));
};

export const transformScheduleDefinitionGroupsToSchedules = (
  groups: ScheduleDefinitionGroup[],
  calendarDate: string,
  options?: {
    scheduleExceptions?: ScheduleException[];
    entityType?: ScheduleExceptionEntityType;
    timezone?: string;
  }
): CalendarEntitySchedule[] => {
  return groups.map<CalendarEntitySchedule>(({ entityId, locationId = '', scheduleDefinitions = [] }) => {
    const resolvedScheduleDefinitions = scheduleDefinitions
      .filter((scheduleDefinition) => doesScheduleDefinitionApplyOnDate(scheduleDefinition, calendarDate))
      .map(toScheduleDefinitionBlock)
      .filter((block): block is CalendarStripeLineInfo => block !== null)
      .sort((left, right) => left.startTime.localeCompare(right.startTime));

    const availabilityExceptions =
      options?.scheduleExceptions && options.entityType
        ? getEntityAvailabilityExceptionsFromScheduleExceptions({
            scheduleExceptions: options.scheduleExceptions,
            entityId,
            entityType: options.entityType,
            calendarDate,
            timezoneId: options.timezone || dayjs.tz.guess(),
          })
        : [];
    return {
      id: entityId,
      locationId,
      resolvedScheduleDefinitions,
      resolvedExceptions: availabilityExceptions,
    };
  });
};
